import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Users, Plus, Trash2, Eye } from 'lucide-react';

const RULE_FIELDS = [
  { value: 'status', label: 'Estado del lead', type: 'select', options: ['active', 'new', 'won', 'lost'] },
  { value: 'has_phone', label: 'Tiene WhatsApp', type: 'boolean' },
  { value: 'interest_contains', label: 'Interés contiene', type: 'text' },
];

export default function MarketingSegments() {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);

  const load = () => { setLoading(true); api.get('/tenant/marketing/segments').then(r => setSegments(r.data || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  return (
    <div className="space-y-4" data-testid="marketing-segments">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-[#0F172A]">Segmentos CRM</h2>
        <Button onClick={() => setShowEditor(true)} className="bg-[#0F172A] text-white" data-testid="segment-create-btn">
          <Plus className="w-4 h-4 mr-1" /> Crear segmento
        </Button>
      </div>
      <p className="text-xs text-[#64748B]">Crea grupos de leads desde tu CRM para usar en campañas. Sin CSV, directo desde tus datos.</p>
      {segments.length === 0 && !loading ? (
        <div className="text-center py-12 bg-white border border-[#E2E8F0] rounded-xl">
          <Users className="w-10 h-10 text-[#CBD5E1] mx-auto mb-3" />
          <p className="text-sm text-[#64748B]">Crea tu primer segmento para agrupar leads por criterios</p>
        </div>
      ) : (
        <div className="space-y-2">
          {segments.map(s => (
            <div key={s.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#0F172A]">{s.name}</p>
                <p className="text-xs text-[#64748B]">{s.estimated_count} leads · {s.rules?.length || 0} reglas</p>
              </div>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => window.location.href = '/tenant/marketing/create'}>Usar en campaña</Button>
                <Button variant="ghost" size="sm" onClick={async () => { await api.delete(`/tenant/marketing/segments/${s.id}`); load(); }}><Trash2 className="w-3.5 h-3.5 text-[#94A3B8]" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {showEditor && <SegmentEditor onClose={() => setShowEditor(false)} onSaved={load} />}
    </div>
  );
}

function SegmentEditor({ onClose, onSaved }) {
  const [name, setName] = useState('');
  const [rules, setRules] = useState([{ field: 'status', operator: 'eq', value: 'active' }]);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const addRule = () => setRules([...rules, { field: 'has_phone', operator: 'eq', value: true }]);
  const removeRule = (i) => setRules(rules.filter((_, idx) => idx !== i));
  const updateRule = (i, k, v) => setRules(rules.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  const previewCount = async () => {
    try { const { data } = await api.post('/tenant/marketing/segments/preview/preview-count', { name: name || 'temp', rules }); setPreview(data.estimated_count); }
    catch { setPreview(null); }
  };

  const save = async () => {
    setSaving(true);
    try { await api.post('/tenant/marketing/segments', { name, rules }); toast.success('Segmento creado'); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="segment-editor">
        <DialogHeader><DialogTitle>Crear segmento</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><label className="text-xs font-semibold text-[#64748B]">Nombre</label>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full mt-1 px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" placeholder="Ej: Leads activos con WhatsApp" /></div>
          <div className="space-y-2" data-testid="segment-rule-builder">
            <label className="text-xs font-semibold text-[#64748B]">Reglas (AND)</label>
            {rules.map((r, i) => {
              const fd = RULE_FIELDS.find(f => f.value === r.field) || RULE_FIELDS[0];
              return (
                <div key={i} className="flex gap-2 items-center">
                  <select value={r.field} onChange={e => updateRule(i, 'field', e.target.value)} className="text-xs border border-[#E2E8F0] rounded px-2 py-1.5 flex-1">
                    {RULE_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}</select>
                  {fd.type === 'select' && <select value={r.value} onChange={e => updateRule(i, 'value', e.target.value)} className="text-xs border border-[#E2E8F0] rounded px-2 py-1.5 flex-1">
                    {fd.options.map(o => <option key={o} value={o}>{o}</option>)}</select>}
                  {fd.type === 'text' && <input value={r.value || ''} onChange={e => updateRule(i, 'value', e.target.value)} className="text-xs border border-[#E2E8F0] rounded px-2 py-1.5 flex-1" />}
                  {fd.type === 'boolean' && <select value={String(r.value)} onChange={e => updateRule(i, 'value', e.target.value === 'true')} className="text-xs border border-[#E2E8F0] rounded px-2 py-1.5">
                    <option value="true">Sí</option><option value="false">No</option></select>}
                  <button onClick={() => removeRule(i)} className="text-[#94A3B8] hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              );
            })}
            <Button variant="outline" size="sm" onClick={addRule}><Plus className="w-3 h-3 mr-1" /> Agregar regla</Button>
          </div>
          <Button variant="outline" size="sm" onClick={previewCount} className="w-full" data-testid="segment-preview-count">
            <Eye className="w-3.5 h-3.5 mr-1" /> {preview !== null ? `Te llegarían aproximadamente ${preview} personas` : 'Ver conteo estimado'}
          </Button>
          <Button onClick={save} disabled={saving || !name} className="w-full bg-[#0F172A] text-white">{saving ? 'Creando...' : 'Crear segmento'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
